/**
 * Helper para sanear los mensajes de error que llegan del servidor.
 *
 * Contexto: el backend responde con varias formas —texto plano, `{ message }`,
 * `{ error }`— y el código las volcaba directamente en las alertas. Eso
 * producía dos fallos:
 *
 *  1. **XSS condicional**: si el backend responde `text/plain` o refleja el
 *     input en el mensaje, el contenido se inyectaba como HTML en `html:` de
 *     SweetAlert2 (que lo interpreta como HTML real).
 *  2. **`[object Object]`**: cuando la respuesta era un objeto
 *     (`{ message: "..." }`), `String(objeto)` producía literalmente
 *     `[object Object]` en pantalla.
 *
 * `extractMessage` resuelve ambos: siempre devuelve una cadena legible y
 * nunca interpretable como HTML.
 */

const DEFAULT_ERROR_MESSAGE = 'Error inesperado';

/**
 * Extrae un mensaje legible de un error de axios (o de cualquier `Error`).
 *
 * Prioridad:
 *   1. `response.data.message` — forma habitual del backend ({ message })
 *   2. `response.data.error`   — variante del backend ({ error })
 *   3. `response.data`          — texto plano
 *   4. `error.message`          — error de red sin respuesta del servidor
 *   5. mensaje genérico
 *
 * @param {*} error  el error capturado en el `catch`.
 * @returns {string} un mensaje de texto plano, nunca `[object Object]`.
 */
export const extractMessage = (error) => {
    if (!error) {
        return DEFAULT_ERROR_MESSAGE;
    }

    const data = error?.response?.data;

    // 1) y 2) — el backend responde con { message } o { error }.
    if (data && typeof data === 'object') {
        const message = data.message ?? data.error;
        if (typeof message === 'string' && message.trim() !== '') {
            return message;
        }
    }

    // 3) — el backend respondió con texto plano.
    if (typeof data === 'string' && data.trim() !== '') {
        return data;
    }

    // 4) — error de red: no hubo respuesta del servidor (o la respuesta vino
    //      sin un mensaje utilizable).
    if (typeof error?.message === 'string' && error.message.trim() !== '') {
        return error.message;
    }

    // 5) — objeto sin mensaje utilizable: se serializa en vez de devolver
    //      "[object Object]". Si sus únicas claves son campos de mensaje
    //      vacíos, no hay nada que mostrar.
    if (data && typeof data === 'object') {
        const keys = Object.keys(data);
        const onlyMessageFields = keys.length > 0 &&
            keys.every((key) => key === 'message' || key === 'error');

        if (!onlyMessageFields) {
            try {
                const serialized = JSON.stringify(data);
                if (serialized && serialized !== '{}') {
                    return serialized;
                }
            } catch (e) {
                // Circular o no serializable: se cae al mensaje genérico.
            }
        }
    }

    return DEFAULT_ERROR_MESSAGE;
};

export { DEFAULT_ERROR_MESSAGE };
